import React, { FC } from 'react';
import { Box, TableCell, Typography, makeStyles, Grid, Link, Paper } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { find, map, uniq, without } from 'lodash';
import { sanitizeAndParseHTML } from 'helpers';

import { generateLink } from 'Links';
import { ContentEmailSpecFragment, UserSegmentFragment } from 'api';

interface IProps {
  segments: UserSegmentFragment[];
  numActiveUsers?: number;
  id: string;
  title?: string;
  segmentIds?: ContentEmailSpecFragment['segment_ids'];
  subject?: string;
  intro?: string;
  contentSpecs?: number;
  postTypes?: ContentEmailSpecFragment['content_specs'];
}

const useStyles = makeStyles(theme => ({
  title: {
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(2),
    display: 'inline-block',
  },
  intro: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
  },
}));

const MessageCell: FC<IProps> = ({
  segments,
  numActiveUsers,
  id,
  title,
  segmentIds,
  subject,
  intro,
  contentSpecs,
  postTypes,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  function renderSegmentNames(emailSegmentIds: ContentEmailSpecFragment['segment_ids']) {
    const segmentList = emailSegmentIds.reduce(
      (list: ContentEmailSpecFragment['segment_ids'], emailId: ContentEmailSpecFragment['id']) => {
        const segment = find(segments, ['id', emailId]);
        return list.concat(segment ? [segment.title] : []);
      },
      []
    );

    if (!segmentList.length) {
      return (
        <Grid item>
          <Paper>
            <Box pl={2} pr={2} pt={1} pb={1}>
              {t('contentEmails:noSegments')}
            </Box>
          </Paper>
        </Grid>
      );
    }

    return segmentList.map((segment: string, ind: number) => (
      <Grid item key={ind}>
        <Paper>
          <Box pl={2} pr={2} pt={1} pb={1}>
            {segment}
          </Box>
        </Paper>
      </Grid>
    ));
  }

  function countEmailUsers(emailSegmentIds: ContentEmailSpecFragment['segment_ids']) {
    if (!emailSegmentIds.length) {
      return numActiveUsers;
    }
    return Math.min(
      emailSegmentIds.reduce((u: number, emailId: ContentEmailSpecFragment['id']) => {
        const segment = find(segments, ['id', emailId]);
        if (segment) {
          u += segment.registered_count + segment.third_party_count;
        }
        return u;
      }, 0),
      numActiveUsers
    );
  }

  function renderNumUsers(emailSegmentIds: ContentEmailSpecFragment['segment_ids']) {
    if (!numActiveUsers) {
      return null;
    }
    const numUsers = countEmailUsers(emailSegmentIds);
    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={3}>
          <Typography variant="body2">{t('messages:recipients')}</Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography>{numUsers}</Typography>
        </Grid>
      </Grid>
    );
  }

  function renderPostTypes(types: ContentEmailSpecFragment['content_specs']) {
    const uniqPostTypes = without(uniq(map(types, 'content_type')), undefined);
    const capitalizedPostTypes = uniqPostTypes.map(s => {
      return s.charAt(0).toUpperCase() + s.slice(1);
    });
    return capitalizedPostTypes.join(', ').replace(/, ([^,]*)$/, ' and $1');
  }

  return (
    <TableCell style={{ width: '50%' }}>
      <Link variant="h6" component={generateLink(`/admin/emails/${id}`)} className={classes.title}>
        {title}
      </Link>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={3}>
          <Typography variant="body2">{t('common:segment_plural')}</Typography>
        </Grid>
        <Grid item xs={9}>
          <Grid container spacing={1} alignItems="center">
            {renderSegmentNames(segmentIds)}
          </Grid>
        </Grid>
      </Grid>
      {renderNumUsers(segmentIds)}
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={3}>
          <Typography variant="body2">{t('common:subject')}</Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography>{subject}</Typography>
        </Grid>
      </Grid>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={3}>
          <Typography variant="body2">{t('common:message')}</Typography>
        </Grid>
        <Grid item xs={9}>
          <div className={classes.intro}>{sanitizeAndParseHTML(intro || '')}</div>
        </Grid>
      </Grid>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={3}>
          <Typography variant="body2">{t('contentEmails:numOfPosts')}</Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography>{contentSpecs}</Typography>
        </Grid>
      </Grid>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={3}>
          <Typography variant="body2">{t('contentEmails:postTypes')}</Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography>{renderPostTypes(postTypes)}</Typography>
        </Grid>
      </Grid>
    </TableCell>
  );
};

export default MessageCell;
